import { ActivityFloatingDock } from "@/components/activity/ActivityFloatingDock";
import { FreshAchievementsToastHost } from "@/components/activity/FreshAchievementsToastHost";
import { FirstLoginWelcomeBanner } from "@/components/activity/FirstLoginWelcomeBanner";
import { LeadWorkspaceShellFireflyOverlay } from "@/components/lead/LeadWorkspaceShellFireflyOverlay";
import { LeadWorkspaceShellModals } from "@/components/lead/LeadWorkspaceShellModals";
import { LeadWorkspaceShellModes } from "@/components/lead/LeadWorkspaceShellModes";
import type { LeadWorkspaceShellModel } from "@/components/lead/useLeadWorkspaceShellProps";
import { useLeadWorkspaceShellFirefly } from "@/components/lead/useLeadWorkspaceShellFirefly";
import { APP_VERSION } from "@/config/version";
import { useManagerDockSignals } from "@/hooks/useManagerDockSignals";
import { useAdminDockSignals } from "@/hooks/useAdminDockSignals";
import { useMfgDockSignals } from "@/hooks/useMfgDockSignals";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import type { AdsAssistantPanelIntent } from "@/components/ads-assistant/adsAssistantPanelUtils";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { canAccessByAnchor } from "@/lib/entitlements/modeAnchorRequirement";
import { useLeadWorkspaceShellDockHandlers } from "@/components/lead/useLeadWorkspaceShellDockHandlers";
import { useWorkspaceModeRequestListener } from "@/components/lead/useWorkspaceModeRequestListener";
import { Sidebar } from "@/components/shell/Sidebar/Sidebar";
import { useSidebarBreakpoint } from "@/components/shell/Sidebar/useSidebarBreakpoint";
import { MobileBottomTabBar } from "@/components/shell/MobileBottomTabBar";
import { TopStrip } from "@/components/shell/TopStrip";
import { OfflineBanner } from "@/components/shell/OfflineBanner";
import { BucketSwitchToast } from "@/components/shell/BucketSwitchToast";
import { shellMobileCardClass, workspaceMainClass } from "@/components/common/surfaceClasses";
import { BucketTransitionPanel } from "@/components/shell/BucketTransitionPanel";
import { resolveActiveBucket } from "@/components/shell/modes/bucketsRegistry";
import { resolveRoleBucketHome } from "@/lib/lastPrimaryModeStorage";
import { WorkspaceModeBanners } from "@/components/shell/WorkspaceModeBanners";
import { WorkspaceToastOverlay } from "@/components/shell/WorkspaceToastOverlay";
import { VoiceFeedbackFirstRunCoachmark } from "@/components/onboarding/VoiceFeedbackFirstRunCoachmark";
import { WorkspaceUrgencyProvider } from "@/contexts/WorkspaceUrgencyContext";
import { useDirtyFormPrompt } from "@/contexts/DirtyFormPromptContext";
import { useAdsHeaderActivity } from "@/hooks/useAdsHeaderActivity";
import { qcReportLinkPending, useQcReportLink } from "@/components/lead/useQcReportLink";
import { useCallback, useEffect, useMemo, useState } from "react";

export function LeadWorkspaceShell(props: LeadWorkspaceShellModel) {
  const [trainingQuickTab, setTrainingQuickTab] = useState<"roleplay" | "ask" | "contacts" | "reviews" | null>(
    null
  );
  const [adsPanelIntent, setAdsPanelIntent] = useState<AdsAssistantPanelIntent>(null);
  const [kbEntrySubModeIntent, setKbEntrySubModeIntent] = useState<"default" | "gap_inbox">("default");
  const {
    mode,
    setMode,
    location,
    toast,
    lastSendAt,
    lastSendStatus,
    queuedCount,
    emptyLeadAutoDeleteEnabled,
    setEmptyLeadAutoDeleteEnabled,
    syncedCount,
    viableCount,
    isSyncing,
    syncAllLeads,
    sendAllUnsent,
    isSending,
    voiceEnabled,
    setVoiceEnabled,
    voiceProvider,
    setVoiceProvider,
    setSpeakMode,
    salesperson,
    setSalesperson,
    showProfileEditor,
    locationsLoaded,
    locations,
    refreshLocations,
    setLocation,
    speakAnswers,
    canSpeakHelp,
    auth,
    locationScopedLeads,
  } = props;

  const scopedOrganizationId = useScopedOrganizationId();
  const { anchors } = useAnchorEntitlements();
  const sidebarVariant = useSidebarBreakpoint();
  const isMobileVariant = sidebarVariant === "mobile";

  // User-initiated mode navigation (Sidebar bucket switcher, mode rail,
  // mobile tab bar, dock CTAs, in-content "open mode X" buttons) routes
  // through `guardedSetMode` so any dirty registered form (Org Settings
  // today; more in follow-on slices per docs plan slice A) gets a
  // Save / Discard / Cancel prompt before the navigation completes.
  // The raw `setMode` stays available for system-initiated jumps that
  // shouldn't surface a prompt — currently just the post-auth
  // last-mode reconciliation effect below.
  const { guardNavigation } = useDirtyFormPrompt();
  const guardedSetMode = useCallback(
    (next: PrimaryMode) => {
      guardNavigation(() => setMode(next));
    },
    [guardNavigation, setMode],
  );
  useWorkspaceModeRequestListener(guardedSetMode);
  useQcReportLink(auth, setMode);
  // Single shell-level fetch feeds the mode-urgency badge (D1), the
  // `ActivityBannerAdAlertsSection` rollup, and the Activity-mode panel
  // (Phase 6). The hook is no-op when the org id is null; otherwise polls
  // every 60s. `refresh` is published through context so the Activity
  // panel's refresh button reuses the same fetch path.
  const { activity: adsActivity, refresh: refreshAdsActivity } = useAdsHeaderActivity(scopedOrganizationId);

  const { kbGapOpenCount, aiPromptingOpenCount } = useManagerDockSignals(
    auth,
    scopedOrganizationId,
  );

  const {
    pendingMemberApprovalCounts,
    pendingMemberApprovalTotal,
    orgUnsentRollupCount,
  } = useAdminDockSignals(auth, scopedOrganizationId);

  const { staleWacCount, plantRequestReviewCount, plantRequestStatusItems, negativeOnHandCount, openQcItems } = useMfgDockSignals(
    auth,
    scopedOrganizationId,
  );

  const openKbGapInbox = useCallback(() => {
    setKbEntrySubModeIntent("gap_inbox");
    guardedSetMode("knowledgebase");
  }, [guardedSetMode]);

  // `auth` is nullable here (pre-bootstrap). Treat the unresolved case as a
  // null bucket id — the transition wrapper's previous-id ref starts at the
  // current value, so a subsequent transition from null → bucket will not
  // animate (intentional; matches BucketSwitchToast's initial-mount guard).
  const activeBucketId = auth ? resolveActiveBucket(mode, auth, anchors) : null;

  // When the current mode becomes inaccessible (impersonation, role change,
  // stale last-mode), land on the role-bucket home. Do not hard-code `leads`
  // — purchasing-only cannot open Sales, and a one-shot ref would miss
  // in-session impersonation.
  useEffect(() => {
    if (!auth) return;
    if (qcReportLinkPending()) return;
    if (canAccessPrimaryMode(mode, auth) && canAccessByAnchor(mode, anchors)) return;
    const fallback = resolveRoleBucketHome(auth, anchors);
    if (fallback && fallback !== mode) setMode(fallback);
  }, [auth, anchors, mode, setMode]);

  // Bell "Ad alerts" row CTA → Ads Assistant → Ingestion. Locked in
  // Phase 6 (the bell is for "log an ad now," not "show me status").
  // The other handlers feed `ActivityFloatingDock` so Slice C's
  // daily-checklist dock section can mirror the navigation CTAs the
  // widget used to surface in the Leads-mode left rail. See
  // docs/planning/org_activity_floating_dock.md §3 Q6 +
  // docs/planning/activity_dock_workflow_facilitation.md §6 Slice C.
  const {
    handleOpenAdsIngestion,
    handleOpenAdsLibrary,
    handleOpenAdsAssistant,
    handleOpenAdsAssistantSuggestion,
    handleOpenSalesConsole,
    followUpAttentionCount,
    handleOpenAiPrompting,
    handleOpenAdmin,
    handleOpenDashboard,
    handleOpenMfgInventory,
    handleOpenMfgPurchasing,
    handleOpenMfgBuilderAssistant,
  } = useLeadWorkspaceShellDockHandlers({
    setMode: guardedSetMode,
    setAdsPanelIntent,
    locationScopedLeads,
    onOpenKbGapInbox: openKbGapInbox,
  });

  const firefly = useLeadWorkspaceShellFirefly({
    mode,
    auth,
    guardedSetMode,
    handleOpenAdsAssistant,
  });

  const leadsControls = useMemo(
    () => ({
      emptyLeadAutoDeleteEnabled,
      setEmptyLeadAutoDeleteEnabled,
      syncedCount,
      viableCount,
      isSyncing,
      onSyncAllLeads: syncAllLeads,
    }),
    [
      emptyLeadAutoDeleteEnabled,
      setEmptyLeadAutoDeleteEnabled,
      syncedCount,
      viableCount,
      isSyncing,
      syncAllLeads,
    ]
  );

  return (
    <WorkspaceUrgencyProvider
      leads={locationScopedLeads}
      adsActivity={adsActivity}
      refreshAdsActivity={refreshAdsActivity}
      currentLead={props.currentLead}
    >
    <div className="flex min-h-screen w-full">
      <Sidebar
        mode={mode}
        setMode={guardedSetMode}
        leadsControls={leadsControls}
        salesperson={salesperson}
        location={location}
        bugReport={{
          devMode: props.devMode,
          requestDevMode: props.requestDevMode,
          disableDevMode: props.disableDevMode,
          getSessionInfo: props.getSessionInfo,
        }}
      />
      <main
        className={
          workspaceMainClass +
          (isMobileVariant ? " pb-20" : "")
        }
      >
        <TopStrip onOpenAdsIngestion={handleOpenAdsIngestion} />
        <OfflineBanner />
        <WorkspaceToastOverlay toast={toast} />
        <BucketSwitchToast mode={mode} />
        <VoiceFeedbackFirstRunCoachmark />
        {/* Layout: mobile (<lg) keeps the centered max-w-3xl card chrome (rationale: feels app-like
            on phones and is what the dev mobile viewport preview should faithfully show).
            Desktop (lg:+) is workstream #4 slice A: drop the card, drop text-center, fill the
            workspace area edge-to-edge with a real app-shell padding ring. */}
        <div className="p-4 lg:px-6 lg:py-5">
          <div className="w-full max-w-6xl mx-auto lg:max-w-none">
            <div className={shellMobileCardClass}>
              <WorkspaceModeBanners
                mode={mode}
                location={location}
                setLocation={setLocation}
                lastSendAt={lastSendAt}
                lastSendStatus={lastSendStatus}
                queuedCount={queuedCount}
                salesperson={salesperson}
                setSalesperson={setSalesperson}
                showProfileEditor={showProfileEditor}
                locationsLoaded={locationsLoaded}
                locations={locations}
                refreshLocations={refreshLocations}
                voiceEnabled={voiceEnabled}
                setVoiceEnabled={setVoiceEnabled}
                voiceProvider={voiceProvider}
                setVoiceProvider={setVoiceProvider}
                speakAnswers={speakAnswers}
                canSpeakHelp={canSpeakHelp}
                setSpeakMode={setSpeakMode}
                kbGapOpenCount={kbGapOpenCount}
                onOpenKbGapInbox={openKbGapInbox}
              />

              <BucketTransitionPanel activeBucketId={activeBucketId}>
                <LeadWorkspaceShellModes
                  model={props}
                  scopedOrganizationId={scopedOrganizationId}
                  trainingQuickTab={trainingQuickTab}
                  setTrainingQuickTab={setTrainingQuickTab}
                  adsPanelIntent={adsPanelIntent}
                  setAdsPanelIntent={setAdsPanelIntent}
                  kbEntrySubModeIntent={kbEntrySubModeIntent}
                  setKbEntrySubModeIntent={setKbEntrySubModeIntent}
                />
              </BucketTransitionPanel>

              <LeadWorkspaceShellModals model={props} />
            </div>
          </div>
        </div>

        {/* Anchored to the bottom of the viewport via main's flex-col +
            mt-auto. When content is short the powered-by hugs the bottom
            of the visible area; when content overflows main's own scroll
            takes over and the powered-by sits at the natural scroll-end.
            Per dev sync note af7c50ef. */}
        <p className="mt-auto text-center text-xs text-gray-400 px-4 pb-3">
          Powered by <span className="font-medium">Turbo Sign Co</span> · v{APP_VERSION}
        </p>
      </main>
      {isMobileVariant ? <MobileBottomTabBar mode={mode} setMode={guardedSetMode} /> : null}
      {/*
        Shell-level Org Activity dock (Phase 6 — see
        docs/planning/org_activity_floating_dock.md). Lives outside the main
        column so it floats over content regardless of which mode is active.
        The component self-gates on role + scopedOrganizationId and is a
        no-op render in unsupported contexts.
      */}
      <ActivityFloatingDock
        onOpenAdsIngestion={handleOpenAdsIngestion}
        onOpenAdsLibrary={handleOpenAdsLibrary}
        onOpenAdsAssistant={handleOpenAdsAssistant}
        onOpenAdsAssistantSuggestion={handleOpenAdsAssistantSuggestion}
        onOpenSalesConsole={handleOpenSalesConsole}
        followUpAttentionCount={followUpAttentionCount}
        onSendAllUnsent={sendAllUnsent}
        isSending={isSending}
        currentMode={mode}
        onOpenPlatform={() => guardedSetMode("platform")}
        kbGapOpenCount={kbGapOpenCount}
        onOpenKbGapInbox={openKbGapInbox}
        aiPromptingOpenCount={aiPromptingOpenCount}
        onOpenAiPrompting={handleOpenAiPrompting}
        pendingMemberApprovalCounts={pendingMemberApprovalCounts}
        pendingMemberApprovalTotal={pendingMemberApprovalTotal}
        onOpenAdmin={handleOpenAdmin}
        orgUnsentRollupCount={orgUnsentRollupCount}
        onOpenDashboard={handleOpenDashboard}
        staleWacCount={staleWacCount}
        onOpenMfgInventory={handleOpenMfgInventory}
        plantRequestReviewCount={plantRequestReviewCount}
        negativeOnHandCount={negativeOnHandCount}
        plantRequestStatusItems={plantRequestStatusItems}
        onOpenMfgPurchasing={handleOpenMfgPurchasing}
        openQcItems={openQcItems}
        onOpenMfgBuilderAssistant={handleOpenMfgBuilderAssistant}
        onNavigateToMode={firefly.handleNavigateFromAskFirefly}
        scopedOrganizationId={scopedOrganizationId}
        onHighlightFireflyTarget={
          firefly.tourSession.isActive ? undefined : firefly.setFireflyAskHighlight
        }
        onStartFireflyTour={firefly.handleStartFireflyTour}
        tourDockOverride={
          firefly.fireflyAskHighlight != null
            ? { active: true, intent: "teaching" }
            : firefly.tourSession.dockOverride
        }
      />
      <LeadWorkspaceShellFireflyOverlay mode={mode} firefly={firefly} />
      {/*
        Shell-level achievement celebration surfaces (Phase 7 — see
        docs/planning/gamification_phase_7_achievement_expansion.md).
        The toast host renders the front-of-queue fresh achievement; the
        welcome banner consumes `user_first_login_ever` exclusively (the
        toast host suppresses that key so we don't double-celebrate).
      */}
      <FreshAchievementsToastHost />
      <FirstLoginWelcomeBanner />
    </div>
    </WorkspaceUrgencyProvider>
  );
}
